import { useState, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

export default function FreeTrial() {
  const { t } = useTranslation();
  const [form, setForm] = useState({ fullName: '', phone: '', country: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const body = new URLSearchParams({ fullName: form.fullName, phone: form.phone, country: form.country });
      await fetch('https://readdy.ai/api/form/d71c828hb0rqicn68et0', { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: body.toString() });
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="test" className="py-6 md:py-8 bg-gradient-to-br from-sky-950/20 to-black">
      <div className="max-w-7xl mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8 sm:mb-12 lg:mb-16 px-4">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6 text-white">{t('trial.title')}</h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-400 max-w-2xl mx-auto">{t('trial.desc')}</p>
            <div className="mt-4 inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs sm:text-sm px-4 py-2 rounded-full">
              <i className="ri-information-line" />
              <span>No credit card required — No automatic charge — Trial ends automatically after 24h</span>
            </div>
          </div>
          <div className="flex justify-center mb-12">
            <div className="bg-gray-800 border border-gray-700 rounded-lg max-w-md w-full p-6">
              <h3 className="font-semibold text-white text-2xl text-center mb-6">{t('trial.form_title')}</h3>
              {submitted ? (
                <div className="text-center py-8">
                  <i className="ri-check-circle-line text-emerald-400 text-5xl mb-4 block" />
                  <p className="text-white font-semibold text-lg">{t('trial.success_title')}</p>
                  <p className="text-gray-400 text-sm mt-2">{t('trial.success_desc')}</p>
                </div>
              ) : (
                <form data-readdy-form className="space-y-4" onSubmit={handleSubmit}>
                  <div>
                    <label className="text-sm font-medium text-white block mb-1">{t('trial.name_label')}</label>
                    <input name="fullName" className="w-full h-10 rounded-md border border-gray-600 bg-gray-700 text-white placeholder:text-gray-400 px-3 py-2 text-sm" placeholder={t('trial.name_placeholder')} maxLength={100} required value={form.fullName} onChange={e => setForm(f => ({ ...f, fullName: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-white block mb-1">{t('trial.phone_label')}</label>
                    <input name="phone" type="tel" className="w-full h-10 rounded-md border border-gray-600 bg-gray-700 text-white placeholder:text-gray-400 px-3 py-2 text-sm" placeholder={t('trial.phone_placeholder')} maxLength={13} required value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-white block mb-1">{t('trial.city_label')}</label>
                    <input name="country" className="w-full h-10 rounded-md border border-gray-600 bg-gray-700 text-white placeholder:text-gray-400 px-3 py-2 text-sm" placeholder={t('trial.city_placeholder')} maxLength={100} required value={form.country} onChange={e => setForm(f => ({ ...f, country: e.target.value }))} />
                  </div>
                  <button id="btn_whatsapp" type="submit" disabled={loading} className="w-full h-10 rounded-md bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 text-white font-semibold text-sm transition-all cursor-pointer whitespace-nowrap disabled:opacity-60">
                    {loading ? t('trial.sending') : t('trial.submit')}
                  </button>
                  <div className="flex items-center gap-3 my-1">
                    <div className="flex-1 border-t border-gray-600" />
                    <span className="text-gray-500 text-xs">or</span>
                    <div className="flex-1 border-t border-gray-600" />
                  </div>
                  <a
                    href="mailto:onetvservices@gmail.com"
                    className="w-full h-10 rounded-md bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white font-semibold text-sm transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                  >
                    <i className="ri-mail-line text-base" />
                    Contact via Email
                  </a>
                </form>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 px-4">
            {[
              { icon: 'ri-time-line', h: 'trial.h1', d: 'trial.h1d' },
              { icon: 'ri-user-unfollow-line', h: 'trial.h2', d: 'trial.h2d' },
              { icon: 'ri-flashlight-line', h: 'trial.h3', d: 'trial.h3d' },
            ].map(({ icon, h, d }) => (
              <div key={h} className="flex items-start space-x-4">
                <div className="bg-gradient-to-r from-orange-500/20 via-sky-500/20 to-blue-500/20 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className={`${icon} text-pink-400 text-xl`} />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">{t(h)}</h3>
                  <p className="text-gray-400">{t(d)}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-12 p-6 bg-gray-800/50 rounded-lg border border-gray-700">
            <h3 className="text-xl font-semibold text-white mb-4 text-center">{t('trial.what_title')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
              {['trial.w1','trial.w2','trial.w3'].map(key => {
                const [title, sub] = t(key).split('\n');
                return (
                  <div key={key} className="text-gray-300">
                    <strong className="text-pink-500 block mb-1">{title}</strong>{sub}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
